import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { FirstLoginResponse } from '@pillar-portal/api-types';
import { authApi } from '@/lib/pillarApi';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import { invalidateSessionSensitiveRequests } from '@/lib/sessionSensitiveRequestManager';
import { useAuthStore } from '@/store/auth';

export interface FirstLoginContentProps {
    readonly token: string;
}

export function FirstLoginContent({ token }: FirstLoginContentProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const logout = useAuthStore((state) => state.logout);
    const redemptionStarted = useRef(false);
    const redemptionMutation = useMutation({
        mutationFn: async (): Promise<FirstLoginResponse> =>
            await authApi.redeemFirstLoginInvitation({ token }),
        onSuccess: async (response: FirstLoginResponse): Promise<void> => {
            queryClient.clear();
            logout();
            invalidateSessionSensitiveRequests();
            if (response.requires_mfa_setup || response.requires_totp_setup) {
                const activationTarget =
                    response.mfa_setup_activation_target ?? response.totp_setup_activation_target;
                await navigate(buildRequiredTotpSetupPath(activationTarget), { replace: true });
                return;
            }
            if (response.requires_mfa || response.requires_totp) {
                const searchParams = new URLSearchParams();
                if (response.available_mfa_methods.length > 0) {
                    searchParams.set('mfa_methods', response.available_mfa_methods.join(','));
                }
                const query = searchParams.toString();
                await navigate(`/login/totp${query.length === 0 ? '' : `?${query}`}`, {
                    replace: true,
                });
                return;
            }
            window.location.assign('/dashboard');
        },
    });

    useEffect(() => {
        if (redemptionStarted.current) {
            return;
        }
        redemptionStarted.current = true;
        redemptionMutation.mutate();
    }, [redemptionMutation.mutate]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full">
                <CardHeader className="space-y-4 text-center">
                    <div className="flex justify-center">
                        <AnchorbaseLogo width={220} />
                    </div>
                    <CardTitle>Finish signing in</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {redemptionMutation.isPending && (
                        <p className="text-center text-sm text-muted-foreground">
                            Verifying your invitation…
                        </p>
                    )}
                    {redemptionMutation.isError && (
                        <>
                            <Alert variant="destructive">
                                <AlertDescription>
                                    This invitation is invalid or expired. Request a new sign-in
                                    invitation to continue.
                                </AlertDescription>
                            </Alert>
                            <Button className="w-full" onClick={() => navigate('/login')}>
                                Go to sign in
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
